import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { recordRoute } from '@adrodd/core';
import { Providers } from './providers';
import ErrorBoundary from './components/ErrorBoundary';
import { RedirectToGrammarRules } from './components/Admin/GrammarRules/RedirectToGrammarRules';

// Lazy load pages for better performance
import { Suspense } from 'react';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Loading component
const PageLoader = () => (
  <div className="p-6 space-y-4 animate-pulse">
    <div className="h-6 bg-muted rounded-xl w-48" />
    <div className="h-4 bg-muted rounded-xl w-full" />
    <div className="h-4 bg-muted rounded-xl w-5/6" />
    <div className="h-4 bg-muted rounded-xl w-3/4" />
  </div>
);

const ContributorsRedirect = () => (
  <Navigate to={{ pathname: '/admin/contributors', search: useLocation().search }} replace />
);

// Lazy loaded pages
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const SignupPage = lazyWithRetry(() => import('./pages/SignupPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const ConfirmEmailPage = lazyWithRetry(() => import('./pages/ConfirmEmailPage'));
const AuthCallbackPage = lazyWithRetry(() => import('./pages/AuthCallbackPage'));
const BetaDownloadPage = lazyWithRetry(() => import('./pages/BetaDownloadPage'));
const GiftPage = lazyWithRetry(() => import('./pages/GiftPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/TermsOfServicePage'));
const RefundsPage = lazyWithRetry(() => import('./pages/RefundsPage'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const WorkWithUsPage = lazyWithRetry(() => import('./pages/WorkWithUsPage'));
const RootPage = lazyWithRetry(() => import('./pages/RootPage'));

// Protected route wrapper
const ProtectedRoute = lazyWithRetry(() => import('./components/ProtectedRoute').then(module => ({ default: module.ProtectedRoute })));

// Authenticated layout
const AuthenticatedLayout = lazyWithRetry(() => import('./layouts/AuthenticatedLayout'));
const PublicToolLayout = lazyWithRetry(() => import('./layouts/PublicToolLayout'));
const AdminLayout = lazyWithRetry(() => import('./layouts/AdminLayout'));
const YourWorkPage = lazyWithRetry(() => import('./pages/YourWorkPage'));
const AuthorChapterEditorPage = lazyWithRetry(() => import('./components/AuthorPortal/AuthorChapterEditorPage'));
const StoriesPage = lazyWithRetry(() => import('./components/User/NewStoriesPage'));
const ExplorePage = lazyWithRetry(() => import('./pages/ExplorePage'));
const ChapterPlayerPage = lazyWithRetry(() => import('./components/User/ChapterDetailPage'));
const ChapterVocabularyPage = lazyWithRetry(() =>
  import('./components/User/ChapterVocabularyPage').then((m) => ({ default: m.ChapterVocabularyPage })),
);
const ProfilePage = lazyWithRetry(() => import('./components/User/ProfilePage').then(module => ({ default: module.ProfilePage })));
const ReviewPage = lazyWithRetry(() => import('./pages/ReviewPage'));
const SavedWordsPage = lazyWithRetry(() => import('./pages/SavedWordsPage'));
const NurseryPage = lazyWithRetry(() => import('./pages/NurseryPage'));

// Admin pages
const AdminDashboardPage = lazyWithRetry(() => import('./components/Admin/AdminDashboardPage'));
const AdminDefinitionsPage = lazyWithRetry(() => import('./components/Admin/AdminDefinitionsPage'));
const AdminStoriesPage = lazyWithRetry(() => import('./components/Admin/AdminStoriesPage'));
const AdminContributorsPage = lazyWithRetry(() => import('./components/Admin/AdminContributorsPage'));
const AdminReviewerDetailPage = lazyWithRetry(() => import('./components/Admin/AdminReviewerDetailPage'));
const AdminIssueLogPage = lazyWithRetry(() => import('./components/Admin/AdminIssueLogPage'));
const AdminUsersPage = lazyWithRetry(() => import('./components/Admin/AdminUsersPage'));
const AdminEditChapterPage = lazyWithRetry(() => import('./components/Admin/AdminEditStoryPage'));
const AdminComponentsPage = lazyWithRetry(() => import('./pages/admin/AdminComponentsPage'));
const AdminChangelogPage = lazyWithRetry(() => import('./components/Admin/AdminChangelogPage'));
const AdminAuthSettingsPage = lazyWithRetry(() => import('./components/Admin/AdminAuthSettingsPage'));
const AdminLevelRubricsPage = lazyWithRetry(() => import('./components/Admin/AdminLevelRubricsPage'));
const AdminLearningRulesPage = lazyWithRetry(() => import('./components/Admin/AdminLearningRulesPage'));
const AdminGrammarRulesPage = lazyWithRetry(() => import('./components/Admin/AdminGrammarRulesPage'));
const AdminWordRankingsPage = lazyWithRetry(() => import('./components/Admin/AdminWordRankingsPage'));
const AdminJobsPage = lazyWithRetry(() => import('./components/Admin/AdminJobsPage'));
const AdminBackupsPage = lazyWithRetry(() => import('./components/Admin/AdminBackupsPage'));
const AdminPerformancePage = lazyWithRetry(() => import('./components/Admin/AdminPerformancePage'));
const AdminTrafficPage = lazyWithRetry(() => import('./components/Admin/AdminTrafficPage'));
const AdminBugReportsPage = lazyWithRetry(() => import('./components/Admin/AdminBugReportsPage'));
const AdminLogsPage = lazyWithRetry(() => import('./components/Admin/AdminLogsPage'));
const AdminBlogPage = lazyWithRetry(() => import('./components/Admin/AdminBlogPage'));

const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const WelshMutationsPage = lazyWithRetry(() => import('./pages/WelshMutationsPage'));
const AuthorValidationPage = lazyWithRetry(() => import('./components/Author/AuthorValidationPage'));
const ToolsPage = lazyWithRetry(() => import('./pages/ToolsPage'));

export function App() {
  const { pathname } = useLocation();
  useEffect(() => recordRoute(pathname), [pathname]);

  return (
    <ErrorBoundary>
      <Providers>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<RootPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          {/* Alias: older Discord error redirects target /auth/discord */}
          <Route path="/auth/discord" element={<AuthCallbackPage />} />
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/refunds" element={<RefundsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/work-with-us" element={<WorkWithUsPage />} />

          {/* Sign-in-first now: the Discord DM tells an approved tester to sign
              in (Discord OAuth creates the account on first login), then find
              the download from their profile menu. This sits outside
              AuthenticatedLayout — that layout bounces every anonymous visitor
              straight to "/" before ProtectedRoute's own /login redirect ever
              runs, which is wrong here: a stranger following a DM/waitlist
              link should land on /login (with a reason), not the marketing
              page. BetaDownloadPage renders its own full chrome, so it needs
              nothing from the authenticated shell. */}
          <Route path="/beta" element={<ProtectedRoute><BetaDownloadPage /></ProtectedRoute>} />

          {/* Public author tools — usable without an account (e.g. validator) */}
          <Route element={<PublicToolLayout />}>
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/validate" element={<AuthorValidationPage />} />
            <Route path="/grammar/mutations" element={<WelshMutationsPage />} />
          </Route>

          {/* Authenticated routes */}
          <Route element={<AuthenticatedLayout />}>
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/stories/:storyId" element={<StoriesPage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/explore/:storyId" element={<ExplorePage />} />
            <Route path="/stories/:storyId/chapter/:position" element={<ChapterPlayerPage />} />
            <Route path="/stories/:storyId/chapter/:position/vocabulary/:chapterId" element={<ChapterVocabularyPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/saved-words" element={<SavedWordsPage />} />
            <Route path="/nursery" element={<NurseryPage />} />

            {/* Your work: one page for authors and reviewers (tabs when someone is both).
                The old Author home redirects here; the chapter editors keep their
                own routes because emails link to them. */}
            <Route path="/work" element={<ProtectedRoute requireAuthorOrReviewer><YourWorkPage /></ProtectedRoute>} />
            <Route path="/author" element={<Navigate to="/work" replace />} />
          </Route>

          {/* Admin routes - use AdminLayout (sidebar shell, no TopNav) */}
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/definitions" element={<ProtectedRoute requireAdmin><AdminDefinitionsPage /></ProtectedRoute>} />
            <Route path="/admin/stories" element={<ProtectedRoute requireAdmin><AdminStoriesPage /></ProtectedRoute>} />
            <Route path="/admin/contributors" element={<ProtectedRoute requireAdmin><AdminContributorsPage /></ProtectedRoute>} />
            <Route path="/admin/contributors/reviewers/:id" element={<ProtectedRoute requireAdmin><AdminReviewerDetailPage /></ProtectedRoute>} />
            {/* The old Submissions page: keeps ?story=<id> for the review page's "Edit story details" link. */}
            <Route path="/admin/submissions" element={<ContributorsRedirect />} />
            <Route path="/admin/issue-log" element={<ProtectedRoute requireAdmin><AdminIssueLogPage /></ProtectedRoute>} />
            {/* Reviews a chapter in the author's own editor layout (mode="admin")
                rather than a second, admin-only copy of it — see Task 10. The
                param is a chapter id, not a submission id: a story-level
                submission has no chapter and is reviewed inline on the
                Contributors page above instead. */}
            <Route path="/admin/submissions/chapters/:id" element={<ProtectedRoute requireAdmin><AuthorChapterEditorPage mode="admin" /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute requireAdmin><AdminBlogPage /></ProtectedRoute>} />
            <Route path="/admin/auth-settings" element={<ProtectedRoute requireAdmin><AdminAuthSettingsPage /></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute requireAdmin><AdminJobsPage /></ProtectedRoute>} />
            <Route path="/admin/backups" element={<ProtectedRoute requireAdmin><AdminBackupsPage /></ProtectedRoute>} />
            <Route path="/admin/bug-reports" element={<ProtectedRoute requireAdmin><AdminBugReportsPage /></ProtectedRoute>} />
            <Route path="/admin/logs" element={<ProtectedRoute requireAdmin><AdminLogsPage /></ProtectedRoute>} />
            <Route path="/admin/rubrics" element={<ProtectedRoute requireAdmin><AdminLevelRubricsPage /></ProtectedRoute>} />
            <Route path="/admin/how-learning-works" element={<ProtectedRoute requireAdmin><AdminLearningRulesPage /></ProtectedRoute>} />
            <Route path="/admin/grammar-rules" element={<ProtectedRoute requireAdmin><AdminGrammarRulesPage /></ProtectedRoute>} />
            <Route path="/admin/linguistic-notes" element={<ProtectedRoute requireAdmin><RedirectToGrammarRules section="notes" /></ProtectedRoute>} />
            <Route path="/admin/function-words" element={<ProtectedRoute requireAdmin><RedirectToGrammarRules section="words" /></ProtectedRoute>} />
            <Route path="/admin/word-rankings" element={<ProtectedRoute requireAdmin><AdminWordRankingsPage /></ProtectedRoute>} />
            {/* Retired: Entries + Quality merged into one Words page. */}
            <Route path="/admin/dictionary-quality" element={<Navigate to="/admin/definitions" replace />} />
            <Route path="/admin/components" element={<ProtectedRoute requireAdmin><AdminComponentsPage /></ProtectedRoute>} />
            <Route path="/admin/performance" element={<ProtectedRoute requireAdmin><AdminPerformancePage /></ProtectedRoute>} />
            <Route path="/admin/traffic" element={<ProtectedRoute requireAdmin><AdminTrafficPage /></ProtectedRoute>} />
            <Route path="/admin/changelog" element={<ProtectedRoute requireAdmin><AdminChangelogPage /></ProtectedRoute>} />
            {/* Chapter editor shares the admin shell like every other admin
                page — it used to sit outside and hand-roll its own sidebar,
                topbar and nav, which is how it ended up with no theme toggle. */}
            <Route path="/admin/chapters/:id/edit" element={<ProtectedRoute requireAdmin><AdminEditChapterPage /></ProtectedRoute>} />
          </Route>

          {/* Author chapter editor — full-screen, outside the portal shell */}
          <Route path="/author/chapters/:id/edit" element={<ProtectedRoute requireAuthor><AuthorChapterEditorPage /></ProtectedRoute>} />

          {/* Reviewer chapter editor — the same editor, full-screen */}
          <Route path="/reviewer/chapters/:id/edit" element={<ProtectedRoute requireReviewer><AuthorChapterEditorPage mode="reviewer" /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Providers>
    </ErrorBoundary>
  );
}
